/**
 * Cuerpo — MOBILE.
 *
 * Una columna con la jerarquía de §5.3: calidad de la pérdida, masa magra, y sólo después el
 * peso. Los indicadores secundarios (agua, ósea, visceral) están, pero abajo y sin protagonismo,
 * porque la spec pide registrarlos y graficarlos sin darles peso.
 *
 * El formulario de una medición se abre en hoja inferior: se completa de pie, saliendo de la
 * balanza.
 */

import { useEffect, useState, type ReactNode } from 'react';
import {
  AVISO_BIOIMPEDANCIA,
  AVISO_CONDICIONES,
  CONSEJO_MEDICION,
  MIN_LECTURAS_COMPARABLES,
  UMBRAL_PRATIO_BAJO,
  condicionesMixtas,
  formatearNumero,
  type Condiciones,
} from '@mg/core';

import { leyendaDeSerie, notaDePocasLecturas, useCuerpo } from '../features/useCuerpo.ts';
import { HojaInferior } from '../ui/Shell.mobile.tsx';
import { LineaConMedia } from '../ui/charts.tsx';
import { Aviso, Campo, Cifra, Icono, Insignia, Pila, Selector, Vacio, estadosDeRatio } from '../ui/componentes.tsx';
import { useRuta } from '../ui/router.tsx';

const CONDICIONES: { valor: Condiciones; etiqueta: string }[] = [
  { valor: 'ayuno', etiqueta: 'En ayunas' },
  { valor: 'post_entreno', etiqueta: 'Después de entrenar' },
  { valor: 'desconocido', etiqueta: 'No me acuerdo' },
];

export function CuerpoMobile(): ReactNode {
  const c = useCuerpo();
  const { ir, detalle } = useRuta();
  const [midiendo, setMidiendo] = useState(detalle === 'medir');

  // ir('cuerpo', 'medir') es la salida de la foto sin señal: llega acá para cargarla a mano, así
  // que la hoja tiene que estar abierta al llegar y no una pantalla más allá.
  useEffect(() => {
    if (detalle === 'medir') setMidiendo(true);
  }, [detalle]);

  if (c.datos.cargando) return <Vacio>Cargando…</Vacio>;

  const mixtas = condicionesMixtas(c.datos.medidas);
  const notaMagra = notaDePocasLecturas(c.lecturasMagra);

  return (
    <>
      <div className="fila" style={{ gap: 'var(--e2)' }}>
        <button
          type="button"
          className="boton boton--principal crece"
          onClick={() => ir('ingesta')}
          style={{ minHeight: 52 }}
        >
          <Icono nombre="camara" tamano={20} />
          Foto de la balanza
        </button>
        <button
          type="button"
          className="boton crece"
          onClick={() => setMidiendo(true)}
          style={{ minHeight: 52 }}
        >
          A mano
        </button>
      </div>

      {/* El consejo va ANTES de pesarse: al pie del formulario se leía cuando ya te bajaste de
          la balanza y no servía para nada. */}
      <p className="nota--fina">{CONSEJO_MEDICION}</p>

      {mixtas && <Aviso tono="atencion" titulo="Condiciones mezcladas">{AVISO_CONDICIONES}</Aviso>}

      <section className="tarjeta">
        <span className="eyebrow">Calidad de la pérdida · 4 semanas</span>
        {c.pratio.estado === 'ok' && c.pratio.calidad !== null ? (
          <>
            <div className="fila" style={{ alignItems: 'baseline', gap: 6, marginTop: 4 }}>
              <span className="cifra-heroe">{Math.round(c.pratio.calidad)}</span>
              <span className="unidad" style={{ fontSize: 20 }}>
                %
              </span>
              {c.pratio.recomposicion && <Insignia tono="agua">recomposición</Insignia>}
            </div>
            <div style={{ marginTop: 'var(--e3)' }}>
              <Pila
                estados={estadosDeRatio(
                  c.pratio.calidad / 100,
                  20,
                  c.pratio.calidad >= UMBRAL_PRATIO_BAJO ? 'llena' : 'alerta',
                )}
                alto={28}
              />
            </div>
            <p className="nota" style={{ marginTop: 'var(--e3)' }}>
              {c.pratio.detalle}
            </p>
          </>
        ) : (
          <>
            {/* Veinte placas huecas no contaban nada: la pila prometía una proporción que no
                existe todavía. Mientras falten lecturas cuenta lecturas —lo que el texto de abajo
                pide— y ahí sí cada placa es una cosa entera. En los otros estados (el peso no
                bajó, no hay % de grasa) no hay nada contable y no va pila: lo explica el texto. */}
            {c.pratio.estado === 'insuficiente' && (
              <div style={{ marginTop: 'var(--e3)' }}>
                <Pila
                  estados={estadosDeRatio(
                    c.pratio.lecturas / MIN_LECTURAS_COMPARABLES,
                    MIN_LECTURAS_COMPARABLES,
                  )}
                  alto={28}
                  etiqueta={`${c.pratio.lecturas} de ${MIN_LECTURAS_COMPARABLES} lecturas comparables`}
                />
              </div>
            )}
            <p className="nota" style={{ marginTop: 'var(--e3)' }}>
              {c.pratio.detalle}
            </p>
            {/* Un conteo sin salida deja al usuario donde estaba: el botón es la salida. Sin racha
                ni reproche — faltan lecturas, no disciplina. */}
            {c.pratio.estado === 'insuficiente' && (
              <button
                type="button"
                className="boton"
                style={{ marginTop: 'var(--e3)' }}
                onClick={() => setMidiendo(true)}
              >
                Medirme
              </button>
            )}
          </>
        )}
      </section>

      <section className="tarjeta">
        <span className="eyebrow">Masa magra · lo que hay que mantener</span>
        <div className="fila fila--entre" style={{ alignItems: 'flex-end' }}>
          <Cifra valor={c.derivadaUltima?.magraKg ?? null} unidad="kg" />
          {c.tendenciaMagra.enDescenso && <Insignia tono="alerta">bajando</Insignia>}
        </div>
        {/* Una cifra grande se lee como un hecho. Con una sola lectura detrás hay que decir de
            dónde sale, o la app está afirmando una tendencia que no midió. */}
        {notaMagra && (
          <p className="nota--fina" style={{ marginTop: 4 }}>
            {notaMagra}
          </p>
        )}
        <div style={{ marginTop: 'var(--e3)' }}>
          <LineaConMedia puntos={c.composicion.magraKg} alto={110} tono="agua" unidad="kg" />
          <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
            {leyendaDeSerie(c.lecturasMagra)}
          </p>
        </div>

        <hr className="separador" />

        {/* Sin altura, FFMI y FMI son dos rayitas mudas: dicen "no hay dato" donde el problema es
            que falta un dato tuyo. Mejor decirlo con palabras. */}
        {c.altura === null ? (
          <p className="nota">FFMI — falta tu altura. El FMI sale de la misma cuenta.</p>
        ) : (
          <div className="fila fila--entre">
            <div>
              <span className="eyebrow">FFMI</span>
              <Cifra valor={c.derivadaUltima?.ffmi ?? null} />
              {c.banda && <p className="nota--fina">{c.banda.etiqueta}</p>}
            </div>
            {/* Tres letras al lado de FFMI se leen como un typo. La nota al pie dice qué es sin
                robarle el lugar a la cifra, y queda a la par de la banda de FFMI. */}
            <div style={{ textAlign: 'right' }}>
              <span className="eyebrow">FMI</span>
              <Cifra valor={c.derivadaUltima?.fmi ?? null} />
              <p className="nota--fina">la misma cuenta, para la grasa</p>
            </div>
          </div>
        )}

        {c.altura === null && (
          <div style={{ marginTop: 'var(--e3)' }}>
            <Aviso tono="atencion" titulo="Falta tu altura">
              <p>
                El FFMI es masa magra dividida por tu altura al cuadrado. {c.explicacionAltura}
              </p>
              <div style={{ marginTop: 8 }}>
                <button type="button" className="boton boton--chico" onClick={() => ir('ajustes')}>
                  Completarla
                </button>
              </div>
            </Aviso>
          </div>
        )}
      </section>

      <section className="tarjeta">
        <div className="fila fila--entre" style={{ marginBottom: 'var(--e2)' }}>
          <span className="eyebrow">Peso · media de 7 días</span>
          {c.velocidad.porcentajeSemanal !== null && (
            <span
              className="lectura nota--fina"
              style={{ color: c.velocidad.excedeBanda ? 'var(--ambar)' : undefined }}
            >
              {formatearNumero(c.velocidad.porcentajeSemanal, 2)} %/sem
            </span>
          )}
        </div>
        <LineaConMedia puntos={c.composicion.peso} alto={120} unidad="kg" />
        {c.velocidad.estado === 'ok' && (
          <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
            {c.velocidad.detalle}
          </p>
        )}
      </section>

      <section className="tarjeta">
        <span className="eyebrow">Grasa · media de 7 días</span>
        <LineaConMedia puntos={c.composicion.grasaPct} alto={110} tono="ambar" unidad="%" />
        <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
          {AVISO_BIOIMPEDANCIA}
        </p>
      </section>

      {c.derivadas.length > 0 && (
        <section className="tarjeta">
          <span className="eyebrow">Lecturas</span>
          <div className="columna" style={{ gap: 'var(--e2)', marginTop: 'var(--e2)' }}>
            {[...c.derivadas].reverse().slice(0, 8).map((d) => (
              <div key={d.medida.id} className="fila fila--entre">
                <div>
                  <span className="lectura">{d.fecha}</span>{' '}
                  <span className="nota--fina">{d.medida.hora}</span>
                  {d.condiciones !== 'desconocido' && (
                    <>
                      {' '}
                      <Insignia>{d.condiciones === 'ayuno' ? 'ayuno' : 'post'}</Insignia>
                    </>
                  )}
                  {d.medida.fuente === 'ocr' && (
                    <>
                      {' '}
                      <Insignia tono="ambar">foto</Insignia>
                    </>
                  )}
                </div>
                <span className="lectura">
                  {formatearNumero(d.pesoKg)} kg
                  {d.medida.grasa_pct !== null && (
                    <span className="apagado"> · {formatearNumero(d.medida.grasa_pct)} %</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {midiendo && (
        <HojaInferior titulo="Medición" onCerrar={() => setMidiendo(false)}>
          <FormularioMedida c={c} onListo={() => setMidiendo(false)} />
        </HojaInferior>
      )}
    </>
  );
}

function FormularioMedida({
  c,
  onListo,
}: {
  c: ReturnType<typeof useCuerpo>;
  onListo: () => void;
}): ReactNode {
  const b = c.borrador;
  const set = (k: keyof typeof b, v: string) => c.setBorrador({ ...b, [k]: v });
  const confirmar = c.validacion.hallazgos.filter((h) => h.severidad === 'confirmar');
  const errores = c.validacion.hallazgos.filter((h) => h.severidad === 'error');

  return (
    <>
      <div className="fila" style={{ gap: 'var(--e2)' }}>
        <div className="crece">
          <Campo etiqueta="Fecha" texto type="date" value={b.fecha} onChange={(e) => set('fecha', e.target.value)} />
        </div>
        <div style={{ width: 110 }}>
          <Campo etiqueta="Hora" texto type="time" value={b.hora} onChange={(e) => set('hora', e.target.value)} />
        </div>
      </div>

      <Campo
        etiqueta="Peso (kg)"
        inputMode="decimal"
        value={b.peso}
        onChange={(e) => set('peso', e.target.value)}
        placeholder="97.5"
      />

      <div className="fila" style={{ gap: 'var(--e2)' }}>
        <div className="crece">
          <Campo etiqueta="Grasa %" inputMode="decimal" value={b.grasa_pct} onChange={(e) => set('grasa_pct', e.target.value)} />
        </div>
        {/* Cada label dice su unidad: un campo "Muscular" a secas obliga a adivinar si la balanza
            daba kilos o por ciento, y el número se guarda tal como se leyó. */}
        <div className="crece">
          <Campo etiqueta="Masa magra (kg)" inputMode="decimal" value={b.masa_magra} onChange={(e) => set('masa_magra', e.target.value)} />
        </div>
      </div>

      <div className="fila" style={{ gap: 'var(--e2)' }}>
        <div className="crece">
          <Campo etiqueta="Muscular (kg)" inputMode="decimal" value={b.masa_muscular} onChange={(e) => set('masa_muscular', e.target.value)} />
        </div>
        <div className="crece">
          <Campo etiqueta="Ósea (kg)" inputMode="decimal" value={b.masa_osea} onChange={(e) => set('masa_osea', e.target.value)} />
        </div>
      </div>

      <div className="fila" style={{ gap: 'var(--e2)' }}>
        <div className="crece">
          <Campo etiqueta="Agua %" inputMode="decimal" value={b.agua_pct} onChange={(e) => set('agua_pct', e.target.value)} />
        </div>
        <div className="crece">
          <Campo etiqueta="Visceral (índice)" inputMode="decimal" value={b.grasa_visceral} onChange={(e) => set('grasa_visceral', e.target.value)} />
        </div>
      </div>

      <div className="fila" style={{ gap: 'var(--e2)' }}>
        <div className="crece">
          <Campo etiqueta="IMC (kg/m²)" inputMode="decimal" value={b.imc} onChange={(e) => set('imc', e.target.value)} />
        </div>
        <div className="crece">
          <Campo etiqueta="TMB (kcal)" inputMode="numeric" value={b.tmb} onChange={(e) => set('tmb', e.target.value)} />
        </div>
      </div>

      <Selector
        etiqueta="Condiciones"
        value={b.condiciones}
        onChange={(e) => c.setBorrador({ ...b, condiciones: e.target.value as Condiciones })}
      >
        {CONDICIONES.map((x) => (
          <option key={x.valor} value={x.valor}>
            {x.etiqueta}
          </option>
        ))}
      </Selector>
      <p className="nota--fina" style={{ marginTop: 4 }}>
        Ayuno o post-entreno: sin esto la lectura no se puede comparar con las demás.
      </p>

      {errores.length > 0 && (
        <Aviso tono="importante" titulo="No se puede guardar">
          {errores.map((h) => h.mensaje).join(' ')}
        </Aviso>
      )}

      {errores.length === 0 && confirmar.length > 0 && (
        <Aviso tono="atencion" titulo="Revisá esto antes de guardar">
          {confirmar.map((h) => h.mensaje).join(' ')}
          <label className="fila" style={{ marginTop: 'var(--e2)', gap: 8 }}>
            <input
              type="checkbox"
              checked={c.confirmado}
              onChange={(e) => c.setConfirmado(e.target.checked)}
              style={{ width: 22, height: 22 }}
            />
            <span>Está bien, es correcto</span>
          </label>
        </Aviso>
      )}

      {/* Guardar tiene que confirmar o explicar; lo que no puede es quedarse callado. */}
      {c.guardado && (
        <>
          <Aviso tono="info">
            <strong>Guardado</strong> ·{' '}
            <span className="lectura">
              {formatearNumero(c.guardado.peso)} {c.guardado.unidad}
            </span>
          </Aviso>
          <button
            type="button"
            className="boton"
            onClick={() => {
              c.descartarGuardado();
              onListo();
            }}
          >
            Ver las lecturas
          </button>
        </>
      )}

      {c.error && (
        <Aviso tono="importante" titulo="No se pudo guardar">
          {c.error}
        </Aviso>
      )}

      <button
        type="button"
        className="boton boton--principal boton--registrar"
        disabled={!c.puedeGuardar || c.guardando}
        onClick={() => void c.guardar()}
      >
        {c.guardando ? 'Guardando…' : 'Guardar medición'}
      </button>

      {!c.guardado && c.queFalta && (
        <p className="nota--fina" style={{ textAlign: 'center' }}>
          {c.queFalta}
        </p>
      )}
    </>
  );
}
